import { SatellitePoint, CloseApproachEvent, CloseApproachConfig } from '../types/satellite';

/**
 * Download utility for exporting data
 */
function downloadFile(filename: string, content: string, mimeType: string = 'application/json'): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}

/**
 * Export satellite positions as GeoJSON
 */
export function exportSatellitesAsGeoJSON(satellites: SatellitePoint[]): void {
  const features = satellites.map(sat => ({
    type: "Feature" as const,
    geometry: {
      type: "Point" as const,
      coordinates: [sat.lng, sat.lat, sat.alt * 6371] // Convert normalized altitude back to km
    },
    properties: {
      name: sat.name,
      altitude_km: Math.round(sat.alt * 6371 * 100) / 100, // Round to 2 decimal places
      color: sat.color,
      size: sat.size,
    }
  }));
  
  const geoJson = {
    type: "FeatureCollection" as const,
    metadata: {
      generated_at: new Date().toISOString(),
      total_satellites: satellites.length,
      source: "SatTrack Space ATC System",
      coordinate_system: "WGS84",
      altitude_units: "kilometers",
    },
    features,
  };
  
  const filename = `satellites_${new Date().toISOString().split('T')[0]}.geojson`;
  downloadFile(filename, JSON.stringify(geoJson, null, 2), 'application/geo+json');
}

/**
 * Export close approach events as JSON
 */
export function exportCloseApproachEvents(
  events: CloseApproachEvent[], 
  config: CloseApproachConfig
): void {
  const exportData = {
    metadata: {
      generated_at: new Date().toISOString(),
      analysis_config: {
        threshold_km: config.thresholdKm,
        horizon_seconds: config.horizonSec,
        time_step_seconds: config.stepSec,
        spatial_cell_size_km: config.cellKm,
        max_events: config.maxPairs,
      },
      total_events: events.length,
      source: "SatTrack Space ATC System",
      warning: "TLE-based estimates only - not suitable for operational collision avoidance",
    },
    events: events.map(event => ({
      ...event,
      time_to_event_seconds: Math.round(
        (new Date(event.t).getTime() - new Date().getTime()) / 1000
      ),
      severity: getSeverityLevel(event.dKm),
    })),
    summary: {
      critical_events: events.filter(e => e.dKm < 1).length,
      high_risk_events: events.filter(e => e.dKm < 5).length,
      medium_risk_events: events.filter(e => e.dKm < 10).length,
      closest_approach: events.length > 0 ? Math.min(...events.map(e => e.dKm)) : null,
      earliest_event: events.length > 0 ? events.reduce((earliest, event) => 
        new Date(event.t) < new Date(earliest.t) ? event : earliest
      ).t : null,
    }
  };
  
  const filename = `close_approaches_${new Date().toISOString().split('T')[0]}.json`;
  downloadFile(filename, JSON.stringify(exportData, null, 2));
}

/**
 * Export satellite orbital data as CSV
 */
export function exportSatellitesAsCSV(satellites: SatellitePoint[]): void {
  const headers = [
    'Name',
    'Latitude',
    'Longitude', 
    'Altitude_km',
    'Color',
    'Timestamp'
  ];
  
  const timestamp = new Date().toISOString();
  const rows = satellites.map(sat => [
    `"${sat.name}"`,
    sat.lat.toFixed(6),
    sat.lng.toFixed(6),
    (sat.alt * 6371).toFixed(2),
    sat.color,
    timestamp
  ]);
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  const filename = `satellites_${new Date().toISOString().split('T')[0]}.csv`;
  downloadFile(filename, csvContent, 'text/csv');
}

/**
 * Export system status and configuration
 */
export function exportSystemStatus(
  satelliteCount: number,
  eventCount: number,
  config: CloseApproachConfig,
  lastUpdateTime: Date
): void {
  const status = {
    system: {
      name: "SatTrack Space ATC System",
      version: "1.0.0",
      export_time: new Date().toISOString(),
    },
    current_status: {
      total_tracked_satellites: satelliteCount,
      active_close_approach_events: eventCount,
      last_data_update: lastUpdateTime.toISOString(),
      system_status: "operational",
    },
    configuration: {
      collision_detection: config,
      data_sources: [
        "CelesTrak TLE Data",
        "SGP4 Orbital Propagation"
      ],
      coordinate_systems: "ECI/TEME to WGS84"
    },
    capabilities: [
      "Real-time satellite tracking",
      "Close approach detection",
      "Spatial hashing collision analysis", 
      "Ground track visualization",
      "Data export (GeoJSON, CSV, JSON)"
    ],
    limitations: [
      "TLE data accuracy decreases over time",
      "Simplified collision probability model",
      "No atmospheric drag compensation",
      "Limited to public catalog objects"
    ]
  };
  
  const filename = `system_status_${new Date().toISOString().split('T')[0]}.json`;
  downloadFile(filename, JSON.stringify(status, null, 2));
}

/**
 * Get severity level for distance
 */
function getSeverityLevel(distanceKm: number): string {
  if (distanceKm < 1) return 'critical';
  if (distanceKm < 5) return 'high';
  if (distanceKm < 10) return 'medium';
  return 'low';
}